<template>
  <div class="test-loading card">
    <div class="ai-loading">
      <div class="ai-pulse">
        <svg viewBox="0 0 24 24" fill="none" stroke="#d0ab4b" stroke-width="2">
          <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
        </svg>
      </div>
      <h3>Analizando tu perfil vocacional</h3>
      <p>La IA está procesando tus respuestas y generando un análisis profundo de tu futuro profesional.</p>
      
      <div class="ai-steps">
        <div 
          v-for="(step, index) in steps" 
          :key="index"
          class="ai-step"
          :class="{ active: currentStepIndex === index, done: currentStepIndex > index || isFinished }"
        >
          <span class="step-dot"></span>
          {{ step }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TestLoading',
  props: {
    userData: { type: Object, required: true },
    scoreData: { type: Object, required: true },
    answersText: { type: Array, required: true }
  },
  data() {
    return {
      currentStepIndex: 0,
      isFinished: false,
      stepInterval: null,
      steps: [
        'Calculando perfil RIASEC...',
        'Identificando combinación de intereses...',
        'Seleccionando empleos del futuro...',
        'Generando análisis de fortalezas...',
        'Personalizando recomendaciones IBERO...'
      ],
      RIASEC_INFO: {
        R:{name:'Técnico-Realista',short:'Técnico',color:'#185FA5',bg:'#E6F1FB'},
        I:{name:'Investigador',short:'Investigador',color:'#085041',bg:'#E1F5EE'},
        A:{name:'Artístico-Creativo',short:'Creativo',color:'#633806',bg:'#FAEEDA'},
        S:{name:'Social',short:'Social',color:'#27500A',bg:'#EAF3DE'},
        E:{name:'Empresarial',short:'Empresarial',color:'#3C3489',bg:'#EEEDFE'},
        C:{name:'Administrativo',short:'Administración',color:'#712B13',bg:'#FAECE7'},
      },
      AUTH_URL: 'https://services.ibero.edu.co/IA/api/Auth/login',
      AI_URL: 'https://services.ibero.edu.co/IA/api/Gemini/preguntar',
      SAVE_URL: 'https://services.ibero.edu.co/IA/api/Gateway/RequestAI',
      AUTH_CREDS: { usuario: 'IberoAI', password: '4-:A5veV7s3AT3I28{KT6L9<zFs08;`$G' },
      PROGRAMAS: {
        R:{
          pre:['Ingeniería de Software Virtual','Ingeniería de Sistemas Virtual','Ingeniería en Ciencia de Datos Virtual','Ingeniería de Telecomunicaciones Virtual','Seguridad y Salud en el Trabajo Virtual'],
          pos:['Espec. en Ciberseguridad Virtual','Espec. en Analítica y Big Data Virtual','Espec. en Inteligencia Artificial Virtual','Espec. en Gestión de Procesos Logísticos y Redes de Valor Virtual']
        },
        I:{
          pre:['Ingeniería en Ciencia de Datos Virtual','Economía Virtual','Administración en Salud Virtual','Lic. en Matemáticas Virtual'],
          pos:['Espec. en Analítica y Big Data Virtual','Espec. en Inteligencia Artificial Virtual','Maestría en Administración de Empresas MBA Virtual']
        },
        A:{
          pre:['Comunicación Digital Virtual','Diseño Visual Virtual','Lic. en Artes Visuales Virtual','Mercadeo y Publicidad Virtual','Lic. en Educación Artística Virtual','Lic. en Humanidades y Lengua Castellana Virtual'],
          pos:['Espec. en Marketing Digital Virtual','Maestría en Ambientes Digitales para la Educación Virtual','Espec. en Inteligencia de Negocios Virtual']
        },
        S:{
          pre:['Psicología Virtual','Trabajo Social Virtual','Lic. en Educación Infantil Virtual','Fonoaudiología','Lic. en Ciencias Sociales Virtual','Lic. en Educación Especial Virtual'],
          pos:['Espec. en Neuropsicología de la Educación Virtual','Maestría en Educación Virtual','Espec. en Docencia Universitaria','Espec. en Desarrollo Integral de Infancia y Adolescencia Virtual']
        },
        E:{
          pre:['Marketing y Negocios Internacionales','Negocios Internacionales Virtual','Administración de Empresas Virtual','Derecho Virtual','Mercadeo y Publicidad Virtual'],
          pos:['Maestría en Administración de Empresas MBA Virtual','Espec. en Alta Gerencia Virtual','Espec. en Gerencia de Proyectos Virtual','Maestría en Gerencia de Proyectos Virtual']
        },
        C:{
          pre:['Contaduría Pública','Administración Financiera Virtual','Administración de Empresas Virtual','Economía Virtual'],
          pos:['Espec. en Gerencia Financiera Virtual','Espec. en Revisoría Fiscal y Auditoría Forense Virtual','Espec. en Inteligencia de Negocios Virtual','Espec. en Auditoría en Salud Virtual']
        },
      }
    };
  },
  mounted() {
    this.stepInterval = setInterval(() => {
      if (this.currentStepIndex < this.steps.length - 1) {
        this.currentStepIndex++;
      }
    }, 900);
    this.runAnalysis();
  },
  beforeDestroy() {
    if (this.stepInterval) {
      clearInterval(this.stepInterval);
    }
  },
  methods: {
    async getAuthToken() {
      const res = await fetch(this.AUTH_URL, {
        method: 'POST',
        headers: { 'accept': '*/*', 'Content-Type': 'application/json' },
        body: JSON.stringify(this.AUTH_CREDS)
      });
      if (!res.ok) throw new Error(`Auth login fallido (HTTP ${res.status})`);
      const data = await res.json();
      return data.token || data.Token || data.access_token || data.accessToken || String(data);
    },
    getFallbackAnalysis() {
      const { top1, top2 } = this.scoreData;
      const p1 = this.RIASEC_INFO[top1];
      const p2 = this.RIASEC_INFO[top2];

      return {
        PerfilTitulo: `${p1.short} con visión ${p2.short}`,
        PerfilDescripcion: `Tu perfil combina las fortalezas del tipo ${p1.name} con las del tipo ${p2.name}. Esta combinación es especialmente valiosa en el mercado laboral actual, donde los profesionales con múltiples dimensiones son los más demandados.`,
        Fortalezas: ['Capacidad de análisis y resolución de problemas', 'Habilidades de comunicación y trabajo colaborativo', 'Visión estratégica orientada a resultados'],
        Retos: ['Desarrollar habilidades digitales avanzadas para potenciar tu perfil', 'Construir una red profesional sólida desde ahora'],
        Empleos: [
          {Titulo: 'Consultor de Transformación Digital', PorQue: 'Tu perfil mixto te permite ser el puente entre la tecnología y las personas.', Mercado: 'El 72% de empresas ya implementó IA. Demanda de este perfil: +45% en 3 años.', ComoCambiaTech: 'La IA automatiza tareas repetitivas; los consultores ahora trabajan en estrategia.', FormacionIbero: this.PROGRAMAS[top1].pre[0]},
          {Titulo: 'Especialista en Innovación y Proyectos', PorQue: 'Combinas pensamiento creativo con capacidad de ejecución.', Mercado: 'Mercado de gestión de proyectos: $6.6 billones globales en 2030.', ComoCambiaTech: 'Las herramientas de IA aceleran el ciclo de innovación 3x.', FormacionIbero: this.PROGRAMAS[top1].pos[0]},
          {Titulo: 'Analista de Datos con Enfoque Humano', PorQue: 'Tus dimensiones dominantes son perfectas para interpretar datos con perspectiva social.', Mercado: '97 millones de nuevos empleos en IA para 2025 según WEF.', ComoCambiaTech: 'El análisis de datos ya no es técnico puro: requiere interpretación humana.', FormacionIbero: this.PROGRAMAS[top1].pre[1] || this.PROGRAMAS[top1].pre[0]}
        ],
        MensajePersonal: `${this.userData.name}, tu perfil vocacional revela una combinación única de fortalezas que el mercado laboral del futuro necesita urgentemente. Tienes las bases para construir una carrera extraordinaria en Colombia y en el mundo. El camino comienza con una decisión valiente: apostar por tu potencial.`,
        ProximoPaso: `Agenda una visita al campus de IBERO esta semana para conocer el programa que más te interesa y hablar con un orientador vocacional.`
      };
    },
    sanitize(str) {
      return String(str || '').replace(/[<>'";\\]/g, '').trim();
    },
    buildProgramCatalogText(top1, top2) {
      const keys = [...new Set([top1, top2, ...Object.keys(this.PROGRAMAS)])];
      return keys.map(k => {
        const prog = this.PROGRAMAS[k];
        const label = this.RIASEC_INFO[k].name;
        return `  ${label} (${k}):\n    Pregrado: ${prog.pre.join(', ')}\n    Posgrado: ${prog.pos.join(', ')}`;
      }).join('\n');
    },
    async runAnalysis() {
      const { scores, top1, top2, sorted } = this.scoreData;
      const scoresText = sorted.map(([k,v]) => `${this.RIASEC_INFO[k].name}: ${v} pts`).join(', ');
      const answersSum = this.answersText.slice(0, 8).join(' | ');
      const catalogText = this.buildProgramCatalogText(top1, top2);

      const prompt = `Eres un experto en orientación vocacional con 20 años de experiencia, psicólogo especialista en el modelo RIASEC de Holland, y conoces profundamente el mercado laboral latinoamericano 2026-2035.

El estudiante ${this.userData.name} (${this.userData.grado}, ${this.userData.colegio}, ${this.userData.ciudad}) completó el Test de Intereses Profesionales.

RESULTADOS RIASEC:
${scoresText}
Perfil predominante: ${this.RIASEC_INFO[top1].name} (${top1}) + ${this.RIASEC_INFO[top2].name} (${top2})

MUESTRA DE RESPUESTAS:
${answersSum}

CATÁLOGO OFICIAL DE PROGRAMAS IBERO (usa ÚNICAMENTE estos nombres exactos en el campo formacion_ibero):
${catalogText}

INSTRUCCIÓN CRÍTICA: En el campo "formacion_ibero" de cada empleo, debes escribir el nombre EXACTO de uno o dos programas del catálogo anterior, priorizando los del perfil ${top1} o ${top2}. No inventes nombres de programas ni uses variaciones.

Genera un análisis vocacional profundo y personalizado en JSON con esta estructura exacta (sin markdown, solo JSON puro):
{
  "perfil_titulo": "título creativo y motivador del perfil combinado en máximo 6 palabras",
  "perfil_descripcion": "2-3 oraciones describiendo la combinación única de este perfil y su potencial especial",
  "fortalezas": ["fortaleza específica 1 con dato concreto", "fortaleza específica 2", "fortaleza específica 3"],
  "retos": ["área de desarrollo 1 con consejo práctico", "área de desarrollo 2"],
  "empleos": [
    {
      "titulo": "Nombre del empleo del futuro (específico y atractivo)",
      "por_que": "Por qué este empleo encaja perfectamente con su perfil específico (2 oraciones)",
      "mercado": "Dato real del mercado laboral o proyección con cifra concreta",
      "como_cambia_tech": "Cómo la IA y tecnología están transformando esta profesión (1 oración)",
      "formacion_ibero": "Nombre EXACTO del programa del catálogo IBERO (uno o dos, separados por ' o ')"
    },
    {
      "titulo": "Segundo empleo del futuro",
      "por_que": "Por qué encaja con el perfil",
      "mercado": "Dato del mercado",
      "como_cambia_tech": "Impacto de la tecnología",
      "formacion_ibero": "Nombre EXACTO del catálogo IBERO"
    },
    {
      "titulo": "Tercer empleo del futuro",
      "por_que": "Por qué encaja",
      "mercado": "Dato del mercado",
      "como_cambia_tech": "Impacto tech",
      "formacion_ibero": "Nombre EXACTO del catálogo IBERO"
    }
  ],
  "mensaje_personal": "Mensaje motivador y personalizado para ${this.userData.name} de 3-4 oraciones. Menciona su nombre, sus dos perfiles dominantes, y una visión inspiradora de su futuro profesional en Colombia/Latinoamérica.",
  "proximo_paso": "Una acción concreta y específica que ${this.userData.name} puede tomar esta semana para avanzar hacia su carrera"
}`;

      let aiAnalysis = null;
      
      try {
        const token = await this.getAuthToken();

        const resGemini = await fetch(this.AI_URL, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ prompt: prompt })
        });

        if (!resGemini.ok) {
            throw new Error(`Fallo al consultar Gemini (Status: ${resGemini.status})`);
        }

        const dataGemini = await resGemini.json();
        let resultadosIA = null;

        try {
          let raw = dataGemini.respuesta || '';
          if (typeof raw === 'string') {
            raw = raw.replace(/```json|```/g, '').trim();
            const jsonMatch = raw.match(/\{[\s\S]*\}/);
            resultadosIA = jsonMatch ? JSON.parse(jsonMatch[0]) : JSON.parse(raw);
          } else if (typeof raw === 'object' && raw !== null) {
            resultadosIA = raw;
          }
        } catch (innerError) {
          console.warn("Fallo en el parseo intermedio de .respuesta", innerError);
        }

        if (!resultadosIA || !resultadosIA.empleos) {
          throw new Error("La estructura extraída de la IA no contiene las propiedades requeridas.");
        }

        const payload = {
            DestinoTabla: "IberoTestVocacional",
            Metadata: { Origin: window.location.hostname || "PortalWeb" },
            Datos: {
              Nombre: this.sanitize(this.userData.name),
              Email: this.sanitize(this.userData.email),
              tipoDocumento: this.sanitize(this.userData.tipo_documento),
              Documento: this.sanitize(this.userData.numero_documento),
              Telefono: this.sanitize(this.userData.celular),
              Ciudad: this.sanitize(this.userData.ciudad),
              Grado: this.sanitize(this.userData.grado),
              Genero: this.sanitize(this.userData.genero),
              Colegio: this.sanitize(this.userData.colegio),
              ResultadosTipologia: resultadosIA, 
              HistorialRespuestas: this.answersText 
            }
        };

        const resBackend = await fetch(this.SAVE_URL, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(payload)
        });

        if (!resBackend.ok) {
            console.warn(`Fallo en el Gateway al persistir datos (Status: ${resBackend.status})`);
        }

        aiAnalysis = resultadosIA;
      } catch (e) {
        console.error("Error crítico en el flujo de comunicación o parseo. Cargando Fallback local.", e);
        aiAnalysis = this.getFallbackAnalysis();
      }

      if (aiAnalysis) {
          aiAnalysis.PerfilTitulo = aiAnalysis.perfil_titulo || aiAnalysis.PerfilTitulo;
          aiAnalysis.PerfilDescripcion = aiAnalysis.perfil_descripcion || aiAnalysis.PerfilDescripcion;
          aiAnalysis.Fortalezas = aiAnalysis.fortalezas || aiAnalysis.Fortalezas;
          aiAnalysis.Retos = aiAnalysis.retos || aiAnalysis.Retos;
          aiAnalysis.MensajePersonal = aiAnalysis.mensaje_personal || aiAnalysis.MensajePersonal;
          aiAnalysis.ProximoPaso = aiAnalysis.proximo_paso || aiAnalysis.ProximoPaso;
          
          const listaEmpleos = aiAnalysis.empleos || aiAnalysis.Empleos;
          if (Array.isArray(listaEmpleos)) {
              aiAnalysis.Empleos = listaEmpleos.map(emp => ({
                  Titulo: emp.titulo || emp.Titulo,
                  PorQue: emp.por_que || emp.PorQue,
                  Mercado: emp.mercado || emp.Mercado,
                  ComoCambiaTech: emp.como_cambia_tech || emp.ComoCambiaTech,
                  FormacionIbero: emp.formacion_ibero || emp.FormacionIbero
              }));
          }
      }

      this.isFinished = true;
      clearInterval(this.stepInterval);
      
      setTimeout(() => {
        this.$emit('ai-analysis-completed', aiAnalysis);
      }, 1000);
    }
  }
};
</script>

<style scoped>
.ai-loading { text-align: center; padding: 3rem 1rem; }
.ai-pulse { width: 56px; height: 56px; border-radius: 50%; background: var(--gold-bg); border: 3px solid var(--gold); margin: 0 auto 1rem; display: flex; align-items: center; justify-content: center; animation: pulse 1.4s ease-in-out infinite; }
.ai-pulse svg { width: 24px; height: 24px; }

@keyframes pulse {
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.12); opacity: 0.7; }
}

.ai-loading h3 { font-size: 17px; font-weight: 700; color: var(--carbon); margin-bottom: 0.5rem; }
.ai-loading p { font-size: 13px; color: var(--text-sec); line-height: 1.6; max-width: 380px; margin: 0 auto; }
.ai-steps { display: flex; flex-direction: column; gap: 6px; margin-top: 1.5rem; max-width: 320px; margin-left: auto; margin-right: auto; text-align: left; }
.ai-step { display: flex; align-items: center; gap: 10px; font-size: 12px; color: var(--text-sec); opacity: 0.5; transition: opacity 0.4s; }
.ai-step.active { opacity: 1; color: var(--carbon); font-weight: 500; }
.ai-step.done { opacity: 1; color: #1D9E75; }
.step-dot { width: 8px; height: 8px; border-radius: 50%; background: currentColor; flex-shrink: 0; }
</style>
